import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Section, Cards, PLANS_URL } from "@/components/Marketing";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Lock, PlayCircle, CheckCircle2, Activity, Layers, Zap } from "lucide-react";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";
import { trackCtaClick, trackLead } from "../lib/analytics";

// Demo platform's bridge endpoint — accepts the original sandbox-request payload
// byte-for-byte (honeypot website + human_check + rendered_at_ms included).
// The demo platform handles token minting, SHA-256 hashing, demo_tokens
// persistence, and Resend dispatch on their side.
const LIVE_DEMO_URL = "https://demo.forgedopshq.com/demo";
const SANDBOX_REQUEST_URL = "https://demo.forgedopshq.com/api/sandbox-request";

const TILES = [
  {
    no: "01",
    title: "Choose a Role",
    body: "Explore the responsibilities of field users, supervisors, and office teams in the demonstration environment.",
    icon: Layers,
  },
  {
    no: "02",
    title: "Follow a Record",
    body: "Walk through operational forms and see how information becomes part of a shared record.",
    icon: Activity,
  },
  {
    no: "03",
    title: "Review the Handoff",
    body: "Look at how the demonstration connects submissions, review, and follow-up between roles.",
    icon: Zap,
  },
];

const INITIAL = {
  full_name: "",
  company: "",
  email: "",
  role: "",
  fleet_size: "",
};

export default function Demo() {
  useDocumentMeta(SEO_ROUTES["/demo"]);

  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [humanCheck, setHumanCheck] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const renderedAtMs = useRef(Date.now());

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const scrollToForm = () => {
    document
      .getElementById("sandbox-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.company.trim() || !form.email.trim()) {
      toast.error("Full name, company, and email are required.");
      return;
    }
    if (!humanCheck) {
      toast.error("Please confirm you're human before submitting.");
      return;
    }
    // Tiny client-side fast-submit guard (the demo backend won't enforce ours,
    // but this stops casual bots from one-shotting the form).
    if (Date.now() - renderedAtMs.current < 2000) {
      toast.error("Submission was too fast. Please try again.");
      return;
    }
    // Honeypot — silently no-op for bots
    if (website) {
      setSubmittedEmail(form.email.trim());
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    try {
      // Canonical POST → demo platform's bridge endpoint at
      // demo.forgedopshq.com/api/sandbox-request. Body includes the full
      // original 8-key payload (honeypot + human_check + rendered_at_ms
      // preserved — bridge endpoint validates them).
      const payload = {
        full_name: form.full_name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        role: form.role || null,
        fleet_size: form.fleet_size || null,
        website: website || null,
        human_check: humanCheck,
        rendered_at_ms: renderedAtMs.current,
      };
      const res = await fetch(SANDBOX_REQUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail = "Unable to issue sandbox key. Please try again.";
        try {
          const data = await res.json();
          if (data?.detail) detail = typeof data.detail === "string" ? data.detail : detail;
        } catch (_) {}
        throw new Error(detail);
      }
      trackLead(`sandbox-${form.role || "unspecified"}`);
      setSubmittedEmail(form.email.trim());
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.message || "Unable to submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="demo-page" className="w-full">
      {/* ============ SECTION 1 — HERO STRIP ============ */}
      <section
        data-testid="demo-hero"
        className="demo-technical-hero grain-overlay relative border-b border-zinc-900 overflow-hidden bg-black"
      >

        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-black/95" />
        <div className="absolute -bottom-32 -right-32 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(255,107,26,0.12)_0%,transparent_65%)] pointer-events-none" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-[35vh] bg-[#ff6b1a]/80 shadow-[0_0_45px_rgba(255,107,26,0.55)]" />

        {/* Caution-stripe top accent */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[repeating-linear-gradient(135deg,#ff6b1a_0_18px,#0a0a0a_18px_30px)] opacity-80" />

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="flex flex-col gap-8 max-w-5xl">
            <div className="flex items-center gap-3">
              <span
                className="relative inline-flex items-center justify-center h-2.5 w-2.5"
                aria-label="Live operational signal"
              >
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#ff6b1a] opacity-70 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ff6b1a] shadow-[0_0_14px_rgba(255,107,26,0.85)]" />
              </span>
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a]">
                Operations Platform / Product Demonstration
              </span>
            </div>

            <h1
              data-testid="demo-hero-title"
              className="font-display uppercase font-black tracking-[-0.025em] leading-[0.88] text-balance text-[clamp(2.4rem,6.8vw,6.4rem)] drop-shadow-[0_4px_30px_rgba(0,0,0,0.7)]"
            >
              See the Platform Running.
              <br />
              <span className="metallic-text">In Real Time.</span>
            </h1>

            <p className="text-zinc-300 text-base md:text-xl leading-relaxed max-w-3xl">
              Explore a ForgedOps demonstration environment. Pick a role, try a workflow, and see how operational information moves through the system. Demonstration content is separate from your company deployment.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-3">
              <a
                href={LIVE_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="demo-open-live-cta"
                onClick={() => trackCtaClick("Open Live Operational Demo", "demo-hero")}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-[#ff8533] transition-all duration-200 shadow-[0_0_40px_rgba(255,107,26,0.35)] hover:shadow-[0_0_60px_rgba(255,107,26,0.55)]"
              >
                <PlayCircle size={18} />
                Open Live Operational Demo
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <button
                type="button"
                onClick={() => {
                  trackCtaClick("Request 3-Day Sandbox Key", "demo-hero");
                  scrollToForm();
                }}
                data-testid="demo-request-sandbox-cta"
                className="group inline-flex items-center gap-3 px-8 py-4 border border-zinc-500 text-white font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-white/5 hover:border-[#ff6b1a] transition-all duration-300"
              >
                <Lock size={16} />
                Request 3-Day Sandbox Key
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 2 — WHAT YOU'LL SEE ============ */}
      <section
        data-testid="demo-capabilities"
        className="relative border-b border-zinc-900 bg-[#070708]"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="flex flex-col gap-3 mb-12 md:mb-16">
            <div className="flex items-center gap-3">
              <span className="h-px w-14 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a]">
                Inside The Sandbox
              </span>
            </div>
            <h2
              data-testid="demo-capabilities-headline"
              className="font-display uppercase font-black tracking-[-0.025em] leading-[0.9] text-[clamp(1.8rem,4.2vw,3.6rem)] max-w-4xl"
            >
              What You'll See When
              <br />
              <span className="metallic-text">The System Runs.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {TILES.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.no}
                  data-testid={`demo-tile-${t.no}`}
                  className="ops-card bg-[#0e0e10] p-8 md:p-10 flex flex-col gap-5 border-t-2 border-t-transparent hover:border-t-[#ff6b1a] transition-all min-h-[280px]"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-display text-[#ff6b1a] text-[14px] tracking-[0.35em] font-black">
                      {t.no}
                    </span>
                    <span className="h-px flex-1 bg-zinc-800" />
                    <Icon className="text-[#ff6b1a]" size={18} />
                  </div>
                  <h3 className="font-display uppercase text-2xl md:text-3xl font-black tracking-tight">
                    {t.title}
                  </h3>
                  <p className="text-zinc-400 text-sm md:text-[15px] leading-relaxed">
                    {t.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SECTION 3 — REQUEST SANDBOX KEY FORM ============ */}
      <section
        id="sandbox-form"
        data-testid="demo-sandbox-form-section"
        className="grain-overlay relative bg-black overflow-hidden"
      >

        <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(255,107,26,0.10)_0%,transparent_60%)] pointer-events-none" />

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="flex flex-col gap-3 mb-12 text-center">
            <div className="flex items-center justify-center gap-3">
              <Lock className="text-[#ff6b1a]" size={16} />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a]">
                3-Day Operational Sandbox
              </span>
            </div>
            <h2 className="font-display uppercase font-black tracking-[-0.025em] leading-[0.9] text-[clamp(1.8rem,4.5vw,3.8rem)] max-w-4xl mx-auto">
              Request Your
              <br />
              <span className="metallic-text">Operational Sandbox Key.</span>
            </h2>
            <p className="text-zinc-400 text-sm md:text-base max-w-2xl mx-auto pt-2">
              Request sandbox access to explore the Operations Platform. Requests are reviewed by our team; access instructions follow approval.
            </p>
          </div>

          {/* Form card */}
          {!submitted ? (
            <form
              onSubmit={onSubmit}
              data-testid="sandbox-request-form"
              className="bg-[#0a0a0c]/95 backdrop-blur-sm border border-zinc-800 p-7 md:p-10 flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="sandbox-full-name"
                    className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                  >
                    Full Name *
                  </label>
                  <input
                    id="sandbox-full-name"
                    type="text"
                    data-testid="sandbox-input-full-name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    required
                    minLength={2}
                    className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all placeholder:text-zinc-600"
                    placeholder="John Smith"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="sandbox-company"
                    className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                  >
                    Company *
                  </label>
                  <input
                    id="sandbox-company"
                    type="text"
                    data-testid="sandbox-input-company"
                    value={form.company}
                    onChange={update("company")}
                    required
                    className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all placeholder:text-zinc-600"
                    placeholder="Heavy Civil Co"
                  />
                </div>
                <div className="flex flex-col gap-2 md:col-span-2">
                  <label
                    htmlFor="sandbox-email"
                    className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                  >
                    Work Email *
                  </label>
                  <input
                    id="sandbox-email"
                    type="email"
                    data-testid="sandbox-input-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all placeholder:text-zinc-600"
                    placeholder="you@company.com"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="sandbox-role"
                    className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                  >
                    Role
                  </label>
                  <select
                    id="sandbox-role"
                    data-testid="sandbox-input-role"
                    value={form.role}
                    onChange={update("role")}
                    className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all"
                  >
                    <option value="">Select Your Role (Optional)</option>
                    <option value="Owner">Owner</option>
                    <option value="PM">PM / Project Manager</option>
                    <option value="Super">Superintendent</option>
                    <option value="Safety">Safety Director</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="sandbox-fleet"
                    className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                  >
                    Fleet Size
                  </label>
                  <select
                    id="sandbox-fleet"
                    data-testid="sandbox-input-fleet"
                    value={form.fleet_size}
                    onChange={update("fleet_size")}
                    className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all"
                  >
                    <option value="">Select Fleet Size (Optional)</option>
                    <option value="1-10">1–10 Units</option>
                    <option value="11-50">11–50 Units</option>
                    <option value="51+">51+ Units</option>
                  </select>
                </div>
              </div>

              {/* Honeypot */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "-9999px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              >
                <label htmlFor="sandbox-website">Website (Leave Blank)</label>
                <input
                  type="text"
                  id="sandbox-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  style={{ width: "1px", height: "1px" }}
                />
              </div>

              {/* Human check */}
              <label
                data-testid="sandbox-human-check"
                className="group flex items-start gap-3 cursor-pointer select-none border border-zinc-800 bg-[#050506] px-4 py-4 hover:border-zinc-700 transition-all"
              >
                <input
                  type="checkbox"
                  checked={humanCheck}
                  onChange={(e) => setHumanCheck(e.target.checked)}
                  className="mt-[3px] h-4 w-4 flex-shrink-0 appearance-none border border-zinc-600 bg-black checked:bg-[#ff6b1a] checked:border-[#ff6b1a] cursor-pointer transition-all relative
                    checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center checked:after:text-black checked:after:text-[11px] checked:after:font-black"
                />
                <span className="flex flex-col gap-0.5">
                  <span className="font-display uppercase tracking-[0.18em] text-[12px] font-bold text-white">
                    I'm a human, not a bot.
                  </span>
                  <span className="text-[11px] text-zinc-500 leading-snug">
                    Please confirm before requesting access.
                  </span>
                </span>
              </label>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
                <p className="text-zinc-500 text-xs md:text-sm max-w-md leading-relaxed">
                  Our team reviews requests before issuing access.
                  Access timing is confirmed after review.
                </p>
                <button
                  type="submit"
                  data-testid="sandbox-submit-button"
                  disabled={submitting || !humanCheck}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-[#ff8533] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_40px_rgba(255,107,26,0.35)] hover:shadow-[0_0_60px_rgba(255,107,26,0.55)]"
                >
                  {submitting ? (
                    <>Dispatching...</>
                  ) : (
                    <>
                      Request Operational Sandbox Key
                      <ArrowUpRight
                        size={16}
                        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div
              data-testid="sandbox-success-state"
              className="bg-[#0a0a0c]/95 backdrop-blur-sm border border-[#ff6b1a]/50 p-10 md:p-14 flex flex-col items-center gap-5 text-center shadow-[0_0_80px_rgba(255,107,26,0.25)]"
            >
              <CheckCircle2 className="text-[#ff6b1a]" size={42} />
              <h3 className="font-display uppercase font-black text-2xl md:text-3xl tracking-tight max-w-2xl">
                Sandbox Request Received.
              </h3>
              <p className="text-zinc-300 text-base md:text-lg max-w-xl leading-relaxed">
                Your request was received for{" "}
                <span className="text-[#ff6b1a] font-bold">
                  {submittedEmail}
                </span>{" "}
                — check your inbox for access instructions.
              </p>
              <Link
                to="/"
                data-testid="sandbox-back-home"
                className="mt-4 font-display uppercase tracking-[0.18em] text-[11px] text-zinc-400 hover:text-[#ff6b1a] transition-colors"
              >
                ← Back to ForgedOps
              </Link>
            </div>
          )}
        </div>
      </section>
      <div className="forge-site"><Section eyebrow="Choose Your Product" title="Looking for a Different Walkthrough?"><Cards items={[{title:"ForgedOps Plans",body:"Explore the plan-room workflow: drawings, measurements, markup, and revision comparison.",to:PLANS_URL,link:"Visit Plans & Buy Online"},{title:"Your Operations Platform deployment",body:"Discuss your modules, workflows, branding choice, onboarding, and licensing.",to:"/contact?interest=Operations%20Platform",link:"Discuss your platform"}]} /></Section></div>
    </div>
  );
}


